import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { isValidDuration } from "../../utils/validation";

interface DurationSelectorProps {
  selectedDuration?: string;
  onDurationSelect?: (duration: string) => void;
  className?: string;
  error?: string;
  disabled?: boolean;
}

const DurationSelector: React.FC<DurationSelectorProps> = ({
  selectedDuration,
  onDurationSelect,
  className = "",
  error,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customDuration, setCustomDuration] = useState("");
  const [customDurationError, setCustomDurationError] = useState("");

  const handleDurationSelect = (duration: string) => {
    if (disabled) return;

    // Hide custom input when predefined duration is selected
    setShowCustomInput(false);
    setCustomDuration("");
    if (onDurationSelect) {
      onDurationSelect(duration);
    }
  };

  // Check if a custom duration is selected (not one of the predefined options)
  const predefinedDurations = [
    "15",
    "30",
    "45",
    "60",
    "90",
    "120",
    "150",
    "180",
  ];
  const isCustomDurationSelected =
    selectedDuration &&
    !predefinedDurations.includes(selectedDuration) &&
    selectedDuration !== "0";
  const hasValidSelection = Boolean(
    selectedDuration && selectedDuration !== "0"
  );

  const handleCustomInputToggle = useCallback(() => {
    if (disabled) return;

    if (showCustomInput) {
      // Hide input and clear value
      setShowCustomInput(false);
      setCustomDuration("");
    } else {
      // Show input
      setShowCustomInput(true);
    }
  }, [disabled, showCustomInput]);

  const handleCustomDurationChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      // Only allow numbers
      if (/^\d*$/.test(value)) {
        setCustomDuration(value);

        // Clear previous error when user starts typing
        if (customDurationError) {
          setCustomDurationError("");
        }

        // Always call onDurationSelect to update parent state, even for empty values
        if (onDurationSelect) {
          onDurationSelect(value);
        }

        // Validate and show error if invalid (but still update parent state)
        if (value && !isValidDuration(value)) {
          const durationNum = parseInt(value, 10);
          if (durationNum <= 0 || durationNum > 1440) {
            setCustomDurationError(
              t("sessionForm.durationValidationError")
            );
          }
        } else {
          setCustomDurationError("");
        }
      }
    },
    [onDurationSelect, customDurationError]
  );

  // Sync custom duration input with selected duration when it's a custom value
  useEffect(() => {
    if (isCustomDurationSelected) {
      setCustomDuration(selectedDuration || "");
      setShowCustomInput(true);
    }
    // Removed automatic hiding/clearing when predefined duration matches
    // Users should be able to enter any value without interference
  }, [selectedDuration, isCustomDurationSelected]);

  return (
    <div className={`space-y-2 ${className}`} data-testid="duration-selector">
      <p className="font-bold">{t("sessionForm.duration")}</p>

      <div className={`${disabled ? "opacity-50" : ""}`}>
        {/* Primary row - most popular durations */}
        <div className="grid max-w-[476px] grid-cols-[repeat(4,minmax(0,1fr))] gap-4">
          <label
            className={`flex ${
              disabled ? "cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            <input
              type="radio"
              name="duration"
              value="30"
              className="peer hidden"
              checked={
                selectedDuration === "30" &&
                hasValidSelection &&
                !isCustomDurationSelected
              }
              onChange={() => handleDurationSelect("30")}
              disabled={disabled}
            />
            <span className="checkbox-group-item py-0">{t("sessionForm.duration30mins")}</span>
          </label>
          <label
            className={`flex ${
              disabled ? "cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            <input
              type="radio"
              name="duration"
              value="45"
              className="peer hidden"
              checked={
                selectedDuration === "45" &&
                hasValidSelection &&
                !isCustomDurationSelected
              }
              onChange={() => handleDurationSelect("45")}
              disabled={disabled}
            />
            <span className="checkbox-group-item py-0">{t("sessionForm.duration45mins")}</span>
          </label>
          <label
            className={`flex ${
              disabled ? "cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            <input
              type="radio"
              name="duration"
              value="60"
              className="peer hidden"
              checked={
                selectedDuration === "60" &&
                hasValidSelection &&
                !isCustomDurationSelected
              }
              onChange={() => handleDurationSelect("60")}
              disabled={disabled}
            />
            <span className="checkbox-group-item py-0">{t("sessionForm.duration1hr")}</span>
          </label>
          <label
            className={`flex ${
              disabled ? "cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            <input
              type="radio"
              name="duration"
              value="90"
              className="peer hidden"
              checked={
                selectedDuration === "90" &&
                hasValidSelection &&
                !isCustomDurationSelected
              }
              onChange={() => handleDurationSelect("90")}
              disabled={disabled}
            />
            <span className="checkbox-group-item py-0">{t("sessionForm.duration1hr30mins")}</span>
          </label>
        </div>

        {/* Divider */}
        <div className="my-4 max-w-[476px] border-t border-grey-80 dark:border-grey-40" />

        {/* Secondary row - edge case durations */}
        <div className="grid max-w-[476px] grid-cols-[repeat(4,minmax(0,1fr))] gap-4">
          <label
            className={`flex ${
              disabled ? "cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            <input
              type="radio"
              name="duration"
              value="15"
              className="peer hidden"
              checked={
                selectedDuration === "15" &&
                hasValidSelection &&
                !isCustomDurationSelected
              }
              onChange={() => handleDurationSelect("15")}
              disabled={disabled}
            />
            <span className="checkbox-group-item py-0">{t("sessionForm.duration15mins")}</span>
          </label>
          <label
            className={`flex ${
              disabled ? "cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            <input
              type="radio"
              name="duration"
              value="120"
              className="peer hidden"
              checked={
                selectedDuration === "120" &&
                hasValidSelection &&
                !isCustomDurationSelected
              }
              onChange={() => handleDurationSelect("120")}
              disabled={disabled}
            />
            <span className="checkbox-group-item py-0">{t("sessionForm.duration2hrs")}</span>
          </label>
          <label
            className={`flex ${
              disabled ? "cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            <input
              type="radio"
              name="duration"
              value="150"
              className="peer hidden"
              checked={
                selectedDuration === "150" &&
                hasValidSelection &&
                !isCustomDurationSelected
              }
              onChange={() => handleDurationSelect("150")}
              disabled={disabled}
            />
            <span className="checkbox-group-item py-0">{t("sessionForm.duration2hr30mins")}</span>
          </label>
          <label
            className={`flex ${
              disabled ? "cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            <input
              type="radio"
              name="duration"
              value="180"
              className="peer hidden"
              checked={
                selectedDuration === "180" &&
                hasValidSelection &&
                !isCustomDurationSelected
              }
              onChange={() => handleDurationSelect("180")}
              disabled={disabled}
            />
            <span className="checkbox-group-item py-0">{t("sessionForm.duration3hrs")}</span>
          </label>
        </div>
      </div>

      <div className={`${disabled ? "opacity-50" : ""}`}>
        <button
          className={`btn mt-4 transition-all duration-200 ${
            showCustomInput ? "btn-primary" : "btn-secondary"
          }`}
          onClick={handleCustomInputToggle}
          type="button"
          disabled={disabled}
        >
          <span>{t("sessionForm.enterYourDuration")}</span>
        </button>

        {/* Animated custom duration input */}
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            showCustomInput ? "max-h-32 opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="pt-4">
            <div className="max-w-[200px]">
              <label className="block text-sm font-bold text-grey-30 dark:text-grey-90 mb-1">
                {t("sessionForm.customDuration")}
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="customDuration"
                  id="customDuration"
                  placeholder={t("sessionForm.enterDuration")}
                  value={customDuration}
                  onChange={handleCustomDurationChange}
                  disabled={disabled}
                  className={`input pr-16 py-2 ${customDurationError ? 'border-error' : ''} ${disabled ? 'opacity-50' : ''}`}
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-grey-50 dark:text-grey-70 pointer-events-none">
                  {t("sessionForm.minutes")}
                </span>
              </div>
              {customDurationError && (
                <p className="input-error mt-1">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 0C12.4183 0 16 3.58172 16 8C16 10.1217 15.1575 12.1569 13.6572 13.6572C12.1569 15.1575 10.1217 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0ZM8.19043 11.0195C7.86419 10.9542 7.52644 11.0559 7.29004 11.29C7.10286 11.4788 6.99846 11.7342 7 12C6.99846 12.2658 7.10286 12.5212 7.29004 12.71C7.38724 12.7983 7.49893 12.8693 7.62012 12.9199C7.86212 13.0269 8.13788 13.0269 8.37988 12.9199C8.50106 12.8693 8.61276 12.7983 8.70996 12.71C8.89715 12.5212 9.00154 12.2658 9 12C8.99632 11.7352 8.89269 11.4817 8.70996 11.29C8.66244 11.247 8.61195 11.2068 8.55957 11.1699C8.50392 11.1322 8.44348 11.1019 8.37988 11.0801C8.32046 11.0497 8.25652 11.0288 8.19043 11.0195ZM8 3C7.44772 3 7 3.33579 7 3.75V8.25C7 8.66421 7.44772 9 8 9C8.55229 9 9 8.66421 9 8.25V3.75C9 3.33579 8.55229 3 8 3Z"
                      fill="currentColor"
                    />
                  </svg>
                  {customDurationError}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error message for duration selection */}
      {error && (
        <p className="field-error mt-1">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 0C12.4183 0 16 3.58172 16 8C16 10.1217 15.1575 12.1569 13.6572 13.6572C12.1569 15.1575 10.1217 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0ZM8.19043 11.0195C7.86419 10.9542 7.52644 11.0559 7.29004 11.29C7.10286 11.4788 6.99846 11.7342 7 12C6.99846 12.2658 7.10286 12.5212 7.29004 12.71C7.38724 12.7983 7.49893 12.8693 7.62012 12.9199C7.86212 13.0269 8.13788 13.0269 8.37988 12.9199C8.50106 12.8693 8.61276 12.7983 8.70996 12.71C8.89715 12.5212 9.00154 12.2658 9 12C8.99632 11.7352 8.89269 11.4817 8.70996 11.29C8.66244 11.247 8.61195 11.2068 8.55957 11.1699C8.50392 11.1322 8.44348 11.1019 8.37988 11.0801C8.32046 11.0497 8.25652 11.0288 8.19043 11.0195ZM8 3C7.44772 3 7 3.33579 7 3.75V8.25C7 8.66421 7.44772 9 8 9C8.55229 9 9 8.66421 9 8.25V3.75C9 3.33579 8.55229 3 8 3Z"
              fill="currentColor"
            />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
};

export default DurationSelector;
